import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useLang } from '../i18n/translations';
import api from '../lib/api';
import { navyPin } from './PonudaDetail';
import { Button } from '../components/ui/button';
import { MapPin, Navigation, ChevronRight } from 'lucide-react';

export default function Mapa() {
  const { t } = useLang();
  const [offers, setOffers] = useState([]);
  const [types, setTypes] = useState([]);
  const [activeType, setActiveType] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const [o, t] = await Promise.all([api.get('/offers'), api.get('/types')]);
        setOffers(o.data);
        setTypes(t.data);
      } catch (err) {
        console.error('Greška pri učitavanju:', err);
      }
    };
    load();
  }, []);

  const filtered = useMemo(
    () => (activeType === 'all' ? offers : offers.filter((o) => o.typeId === activeType)),
    [offers, activeType]
  );

  return (
    <div className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="text-4xl font-bold text-[#0b1a3d] mb-2" data-testid="mapa-title">{t('map')}</h1>
        <p className="text-slate-500 max-w-2xl mb-6">
          Interaktivni vodič kroz turističku ponudu Srednjobosanskog kantona. Kliknite na oznaku za kratke informacije, detalje i GPS navigaciju.
        </p>

        <div className="flex flex-wrap gap-2 mb-6" data-testid="mapa-filters">
          <button onClick={() => setActiveType('all')} data-testid="mapa-filter-all"
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors duration-200 ${activeType === 'all' ? 'bg-[#001752] text-white border-[#001752]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#001752]/40'}`}>
            {t('allTypes')} ({offers.length})
          </button>
          {types.map((tp) => {
            const count = offers.filter((o) => o.typeId === tp.id).length;
            if (!count) return null;
            return (
              <button key={tp.id} onClick={() => setActiveType(tp.id)} data-testid={`mapa-filter-${tp.id}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors duration-200 ${activeType === tp.id ? 'bg-[#001752] text-white border-[#001752]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#001752]/40'}`}>
                {tp.name} ({count})
              </button>
            );
          })}
        </div>

        <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm" data-testid="mapa-container">
          <MapContainer center={[44.15, 17.65]} zoom={10} style={{ height: '620px', width: '100%' }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
            {filtered.map((o) => (
              <Marker key={o.id} position={[o.lat, o.lng]} icon={navyPin}>
                <Popup maxWidth={280}>
                  <div className="w-64">
                    <img src={o.image} alt={o.title} className="w-full h-28 object-cover rounded-md mb-2" />
                    <p className="font-bold text-[#0b1a3d] leading-snug mb-1">{o.title}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1 mb-1.5"><MapPin className="h-3 w-3" /> {o.city}</p>
                    <p className="text-xs text-slate-600 mb-3">{o.shortDesc}</p>
                    <div className="flex gap-2">
                      <Link to={`/ponuda/${o.id}`} className="flex-1">
                        <Button size="sm" className="w-full bg-[#001752] hover:bg-[#0a2a7a] text-white text-xs h-8 rounded-md">
                          {t('details')} <ChevronRight className="h-3 w-3 ml-1" />
                        </Button>
                      </Link>
                      <a href={`https://www.google.com/maps/dir/?api=1&destination=${o.lat},${o.lng}`} target="_blank" rel="noreferrer">
                        <Button size="sm" variant="outline" className="border-[#001752] text-[#001752] text-xs h-8 rounded-md">
                          <Navigation className="h-3 w-3" />
                        </Button>
                      </a>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        <p className="text-xs text-slate-400 mt-3">
          Prikazano {filtered.length} lokacija na mapi • Podaci: registar turističke ponude SBK/KSB
        </p>
      </div>
    </div>
  );
}
